// Fix in chat for a failed run: a draft the person reviews and sends to the App's own chat. One
// localized request, then the run's own facts verbatim (function, error, its last printed lines and
// its input), so the builder sees exactly what happened. Nothing is sent from here.
import { formatCopy } from '@/i18n/ns';
import type { AppTrackerCopyKey } from '@/i18n/ns/app-tracker';
import type { RunDetail } from '../runs-api';

type Copy = Record<AppTrackerCopyKey, string>;
export const FIX_PROMPT_LIMITS = Object.freeze({ lines: 40, stack: 6, inputBytes: 4096 });

const bytes = (value: string) => new TextEncoder().encode(value).length;

/** A fence longer than any run of backticks inside, so printed text cannot close it early. */
function fenced(body: string, language = 'text') {
  const longest = Math.max(2, ...(body.match(/`+/gu) ?? []).map((run) => run.length));
  const fence = '`'.repeat(longest + 1);
  return `${fence}${language}\n${body}\n${fence}`;
}

function inputText(input: unknown) {
  if (input === null || input === undefined) return null;
  let text: string;
  try {
    text = JSON.stringify(input, null, 2) ?? String(input);
  } catch {
    return null;
  }
  if (bytes(text) <= FIX_PROMPT_LIMITS.inputBytes) return text;
  const compact = JSON.stringify(input);
  let end = Math.min(compact.length, FIX_PROMPT_LIMITS.inputBytes);
  while (end > 0 && bytes(compact.slice(0, end)) > FIX_PROMPT_LIMITS.inputBytes - 3) end--;
  return compact.slice(0, end) + '…';
}

export function buildFixPrompt(
  run: RunDetail,
  copy: Copy,
  { locale, when }: { locale: string; when: string | null },
) {
  const lead =
    run.outcome === 'unknown'
      ? copy.fixUnknown
      : run.trigger === 'schedule'
        ? copy.fixScheduled
        : copy.fixTriggered;
  const parts = [
    lead,
    formatCopy(copy.fixRunLine, {
      name: run.functionId,
      id: run.id,
      time: when ?? new Date(run.finishedAt).toLocaleString(locale),
    }),
  ];
  if (run.error || run.code) {
    const error = run.error
      ? [
          `${run.error.name}: ${run.error.message}`.trim(),
          ...run.error.stack.slice(0, FIX_PROMPT_LIMITS.stack).map((line) => `  ${line}`),
        ]
      : [run.code as string];
    parts.push(`${copy.fixError}:\n${fenced(error.join('\n'))}`);
  }
  const lines = run.logs.lines.slice(-FIX_PROMPT_LIMITS.lines);
  if (lines.length)
    parts.push(
      `${copy.fixPrinted}:\n${fenced(lines.map((line) => `[${line.level}] ${line.text}`).join('\n'))}`,
    );
  const input = inputText(run.input);
  if (input) parts.push(`${copy.fixInput}:\n${fenced(input, 'json')}`);
  return parts.join('\n\n');
}
